// localStorage utility functions for ViewTube

const KEYS = {
  USER_PROFILE: 'viewtube_user_profile',
  FAVORITE_CHANNELS: 'viewtube_favorite_channels',
  WATCH_HISTORY: 'viewtube_watch_history',
  WATCH_LATER: 'viewtube_watch_later',
  LIKED_VIDEOS: 'viewtube_liked_videos',
  THEME: 'viewtube_theme',
};

// User Profile
export const getUserProfile = () => {
  try {
    const profile = localStorage.getItem(KEYS.USER_PROFILE);
    return profile ? JSON.parse(profile) : { name: 'Guest', profilePic: null };
  } catch (error) {
    console.error('Error getting user profile:', error);
    return { name: 'Guest', profilePic: null };
  }
};

export const setUserProfile = (profile) => {
  try {
    localStorage.setItem(KEYS.USER_PROFILE, JSON.stringify(profile));
    return true;
  } catch (error) {
    console.error('Error setting user profile:', error);
    return false;
  }
};

// Favorite Channels
export const getFavoriteChannels = () => {
  try {
    const channels = localStorage.getItem(KEYS.FAVORITE_CHANNELS);
    return channels ? JSON.parse(channels) : [];
  } catch (error) {
    console.error('Error getting favorite channels:', error);
    return [];
  }
};

export const addFavoriteChannel = (channelId, channelTitle) => {
  try {
    const channels = getFavoriteChannels();
    if (!channels.find(c => c.id === channelId)) {
      channels.push({ id: channelId, title: channelTitle, addedAt: Date.now() });
      localStorage.setItem(KEYS.FAVORITE_CHANNELS, JSON.stringify(channels));
    }
    return true;
  } catch (error) {
    console.error('Error adding favorite channel:', error);
    return false;
  }
};

export const removeFavoriteChannel = (channelId) => {
  try {
    const channels = getFavoriteChannels().filter(c => c.id !== channelId);
    localStorage.setItem(KEYS.FAVORITE_CHANNELS, JSON.stringify(channels));
    return true;
  } catch (error) {
    console.error('Error removing favorite channel:', error);
    return false;
  }
};

// Watch History
export const getWatchHistory = () => {
  try {
    const history = localStorage.getItem(KEYS.WATCH_HISTORY);
    return history ? JSON.parse(history) : [];
  } catch (error) {
    console.error('Error getting watch history:', error);
    return [];
  }
};

export const addToWatchHistory = (video) => {
  try {
    const history = getWatchHistory();
    const videoData = {
      id: video.id.videoId || video.id,
      title: video.snippet.title,
      channelTitle: video.snippet.channelTitle,
      channelId: video.snippet.channelId,
      thumbnail: video.snippet.thumbnails.medium?.url || video.snippet.thumbnails.default?.url,
      watchedAt: Date.now(),
    };
    
    // Remove if already exists
    const filtered = history.filter(v => v.id !== videoData.id);
    // Add to beginning
    filtered.unshift(videoData);
    // Keep only last 100
    const limited = filtered.slice(0, 100);
    
    localStorage.setItem(KEYS.WATCH_HISTORY, JSON.stringify(limited));
    return true;
  } catch (error) {
    console.error('Error adding to watch history:', error);
    return false;
  }
};

export const clearWatchHistory = () => {
  try {
    localStorage.removeItem(KEYS.WATCH_HISTORY);
    return true;
  } catch (error) {
    console.error('Error clearing watch history:', error);
    return false;
  }
};

// Watch Later
export const getWatchLater = () => {
  try {
    const watchLater = localStorage.getItem(KEYS.WATCH_LATER);
    return watchLater ? JSON.parse(watchLater) : [];
  } catch (error) {
    console.error('Error getting watch later:', error);
    return [];
  }
};

export const addToWatchLater = (video) => {
  try {
    const watchLater = getWatchLater();
    const videoData = {
      id: video.id.videoId || video.id,
      title: video.snippet.title,
      channelTitle: video.snippet.channelTitle,
      thumbnail: video.snippet.thumbnails.medium?.url || video.snippet.thumbnails.default?.url,
      addedAt: Date.now(),
    };
    
    if (!watchLater.find(v => v.id === videoData.id)) {
      watchLater.unshift(videoData);
      localStorage.setItem(KEYS.WATCH_LATER, JSON.stringify(watchLater));
    }
    return true;
  } catch (error) {
    console.error('Error adding to watch later:', error);
    return false;
  }
};

export const removeFromWatchLater = (videoId) => {
  try {
    const watchLater = getWatchLater().filter(v => v.id !== videoId);
    localStorage.setItem(KEYS.WATCH_LATER, JSON.stringify(watchLater));
    return true;
  } catch (error) {
    console.error('Error removing from watch later:', error);
    return false;
  }
};

export const isInWatchLater = (videoId) => {
  const watchLater = getWatchLater();
  return watchLater.some(v => v.id === videoId);
};

// Liked Videos
export const getLikedVideos = () => {
  try {
    const liked = localStorage.getItem(KEYS.LIKED_VIDEOS);
    return liked ? JSON.parse(liked) : [];
  } catch (error) {
    console.error('Error getting liked videos:', error);
    return [];
  }
};

export const addToLikedVideos = (video) => {
  try {
    const liked = getLikedVideos();
    const videoData = {
      id: video.id.videoId || video.id,
      title: video.snippet.title,
      channelTitle: video.snippet.channelTitle,
      thumbnail: video.snippet.thumbnails.medium?.url || video.snippet.thumbnails.default?.url,
      likedAt: Date.now(),
    };
    
    if (!liked.find(v => v.id === videoData.id)) {
      liked.unshift(videoData);
      localStorage.setItem(KEYS.LIKED_VIDEOS, JSON.stringify(liked));
    }
    return true;
  } catch (error) {
    console.error('Error adding to liked videos:', error);
    return false;
  }
};

export const removeFromLikedVideos = (videoId) => {
  try {
    const liked = getLikedVideos().filter(v => v.id !== videoId);
    localStorage.setItem(KEYS.LIKED_VIDEOS, JSON.stringify(liked));
    return true;
  } catch (error) {
    console.error('Error removing from liked videos:', error);
    return false;
  }
};

export const isLiked = (videoId) => {
  const liked = getLikedVideos();
  return liked.some(v => v.id === videoId);
};

// Theme
export const getTheme = () => {
  try {
    return localStorage.getItem(KEYS.THEME) || 'light';
  } catch (error) {
    console.error('Error getting theme:', error);
    return 'light';
  }
};

export const setTheme = (theme) => {
  try {
    localStorage.setItem(KEYS.THEME, theme);
    document.documentElement.setAttribute('data-theme', theme);
    return true;
  } catch (error) {
    console.error('Error setting theme:', error);
    return false;
  }
};

// Initialize theme on app load
export const initializeTheme = () => {
  const theme = getTheme();
  document.documentElement.setAttribute('data-theme', theme);
};
